// An explanation behind a small info icon, shown on hover and on focus
// (Brúnó, 2026-10-15: what a field means goes behind an info icon, not onto
// the page as text).

import {useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Icon} from '../creator/components/Icon';

export function InfoTip({text, wide = false}: {text: string; wide?: boolean}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [place, setPlace] = useState<{left: number; top: number; above: boolean; width: number} | null>(null);

  // Placed on the page itself, as the admin's InfoTip is: neither the top of
  // the window nor a card's edge cuts it off, and it stays inside a phone's width.
  function show() {
    const box = ref.current?.getBoundingClientRect();
    if (!box) return;
    const width = Math.min(wide ? 320 : 260, window.innerWidth - 16);
    const left = Math.max(8, Math.min(box.left - 12, window.innerWidth - width - 8));
    const above = box.bottom > window.innerHeight - 200;
    setPlace({left, top: above ? box.top - 8 : box.bottom + 8, above, width});
  }
  const hide = useCallback(() => setPlace(null), []);
  useEffect(() => {
    if (!place) return undefined;
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [place, hide]);

  // Inside a label, a click on the icon must not move the focus to the field.
  return <span ref={ref} className="info-tip" tabIndex={0} role="note" aria-label={text}
    onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}
    onClick={(event) => { event.preventDefault(); event.stopPropagation(); }}>
    <Icon name="info" size={14} />
    {place ? createPortal(<span className={`info-tip-bubble${place.above ? ' above' : ''}`} aria-hidden="true"
      style={{left: place.left, top: place.top, maxWidth: place.width}}>{text}</span>, document.body) : null}
  </span>;
}
